'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, designTokens } from '@webapp/ui';

import { createApiClient } from './apiClient';
import { useStoredAuthToken } from './authSession';
import { emitSetupGuideEvent, onSetupGuideEvent } from './setupGuideBus';
import { useVocabulary } from './VocabularyProvider';
import { useI18n } from './i18n';

// ─── Guide state (persisted so a refresh continues the tour) ─────────────────

type GuideStep = 'name' | 'place' | 'done';

type GuideState = {
  step: GuideStep;
  projectSlug: string;
};

const storageKey = 'webapp.setupGuide.v1';
const stateChangedEvent = 'webapp-setup-guide-changed';

function readGuideState(): GuideState | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuideState;
    if (!parsed || typeof parsed.step !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeGuideState(state: GuideState | null): void {
  if (state) window.localStorage.setItem(storageKey, JSON.stringify(state));
  else window.localStorage.removeItem(storageKey);
  window.dispatchEvent(new Event(stateChangedEvent));
}

/** Starts (or restarts) the guided setup. Without a slug the guide begins by naming a new trip. */
export function startSetupGuide(projectSlug = ''): void {
  writeGuideState({ step: projectSlug ? 'place' : 'name', projectSlug });
}

// ─── Spotlight: a ring that follows the highlighted element ──────────────────

function Spotlight({ selector }: { selector: string }) {
  const [box, setBox] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const scrolledToRef = useRef('');

  useEffect(() => {
    let frame = 0;
    const measure = () => {
      const element = document.querySelector(selector);
      if (!element) {
        setBox(null);
      } else {
        if (scrolledToRef.current !== selector) {
          scrolledToRef.current = selector;
          element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        const rect = element.getBoundingClientRect();
        setBox((current) => {
          if (current && Math.abs(current.top - rect.top) < 1 && Math.abs(current.left - rect.left) < 1
            && Math.abs(current.width - rect.width) < 1 && Math.abs(current.height - rect.height) < 1) {
            return current;
          }
          return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
        });
      }
      frame = window.requestAnimationFrame(measure);
    };
    frame = window.requestAnimationFrame(measure);
    return () => window.cancelAnimationFrame(frame);
  }, [selector]);

  if (!box || box.width === 0) return null;
  return (
    <div
      aria-hidden="true"
      style={{
        border: `2px solid ${designTokens.colors.warning}`,
        borderRadius: designTokens.radius.md,
        boxShadow: '0 0 0 4px rgba(255, 204, 102, 0.25), 0 0 24px rgba(255, 204, 102, 0.35)',
        height: box.height + 8,
        left: box.left - 4,
        pointerEvents: 'none',
        position: 'fixed',
        top: box.top - 4,
        transition: 'top 0.15s, left 0.15s, width 0.15s, height 0.15s',
        width: box.width + 8,
        zIndex: 80,
      }}
    />
  );
}

// ─── The guide panel ──────────────────────────────────────────────────────────

type StepContent = {
  title: string;
  text: string;
  spotlight?: string;
  nextLabel?: string;
  progress: number;
};

export function SetupGuide() {
  const navigate = useNavigate();
  const token = useStoredAuthToken();
  const { mode } = useVocabulary();
  const { t } = useI18n();
  const api = useMemo(() => createApiClient(''), []);
  const [guide, setGuide] = useState<GuideState | null>(null);
  const [tripName, setTripName] = useState('');
  const [tripStartAt, setTripStartAt] = useState('');
  const [tripEndAt, setTripEndAt] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setGuide(readGuideState());
    const sync = () => setGuide(readGuideState());
    window.addEventListener(stateChangedEvent, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(stateChangedEvent, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const advance = useCallback((next: GuideState | null) => {
    writeGuideState(next);
  }, []);

  // Progress by doing: the guide ends as soon as the first useful Day exists.
  useEffect(() => {
    if (!guide || guide.step !== 'place') return;
    return onSetupGuideEvent('location-added', () => advance({ ...guide, step: 'done' }));
  }, [guide, advance]);

  // Keep the plan tab visible while the guide points at it.
  useEffect(() => {
    if (guide?.step === 'place') {
      const timer = window.setTimeout(() => emitSetupGuideEvent('open-plan-tab'), 400);
      return () => window.clearTimeout(timer);
    }
  }, [guide?.step]);

  // Wait for a signed-in session — the guide should not float over the login page.
  if (!guide || !token) return null;

  async function createTrip() {
    if (!token || !tripName.trim() || !tripStartAt || !tripEndAt || tripEndAt < tripStartAt || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const project = await api.createProject(token, { name: tripName.trim(), mode, start_at: tripStartAt, end_at: tripEndAt });
      advance({ step: 'place', projectSlug: project.slug });
      navigate(`/projects/${project.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('setup.createFailed'));
    } finally {
      setIsSaving(false);
    }
  }

  function close() {
    advance(null);
  }



  const steps: Record<GuideStep, StepContent> = {
    name: {
      title: t('setup.nameTitle'),
      text: t('setup.nameText'),
      progress: 0,
    },
    place: {
      title: t('setup.placeTitle'),
      text: t('setup.placeText'),
      spotlight: '[data-tour="add-place"]',
      progress: 1,
    },
    done: {
      title: t('setup.doneTitle'),
      text: t('setup.doneText'),
      spotlight: 'nav.dd-trip-nav-desktop',
      nextLabel: t('setup.finish'),
      progress: 2,
    },
  };
  const content = steps[guide.step];
  const stepCount = 3;

  function handleNext() {
    if (guide!.step === 'done') close();
  }

  return (
    <>
      {content.spotlight ? <Spotlight selector={content.spotlight} /> : null}
      <aside
        aria-label={t('setup.guide')}
        style={{
          background: designTokens.colors.surfaceContainerLow,
          border: `1px solid ${designTokens.colors.primaryContainer}`,
          borderRadius: designTokens.radius.lg,
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.35)',
          boxSizing: 'border-box',
          color: designTokens.colors.onSurface,
          maxWidth: 'calc(100vw - 24px)',
          padding: 16,
          position: 'fixed',
          right: 12,
          top: designTokens.layout.navHeight + 10,
          width: 320,
          zIndex: 85,
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 8 }}>
          <div aria-hidden="true" style={{ display: 'flex', gap: 4 }}>
            {Array.from({ length: stepCount }, (_, index) => (
              <span
                key={index}
                style={{
                  background: index <= content.progress ? designTokens.colors.primaryContainer : designTokens.colors.outlineVariant,
                  borderRadius: 3,
                  display: 'inline-block',
                  height: 5,
                  width: 22,
                }}
              />
            ))}
          </div>
          <button
            type="button"
            aria-label={t('setup.close')}
            onClick={close}
            style={{ background: 'transparent', border: 0, color: designTokens.colors.outline, cursor: 'pointer', font: 'inherit', fontSize: 16, lineHeight: 1, padding: 4 }}
          >
            ✕
          </button>
        </div>

        <h2 style={{ fontSize: 17, margin: '0 0 6px' }}>{content.title}</h2>
        <p style={{ color: designTokens.colors.onSurfaceVariant, fontSize: 13, lineHeight: 1.5, margin: 0 }}>{content.text}</p>

        {guide.step === 'name' ? (
          <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
            <Input
              aria-label={t('setup.tripName')}
              autoFocus
              placeholder={t('setup.tripPlaceholder')}
              value={tripName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTripName(event.currentTarget.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => { if (event.key === 'Enter') void createTrip(); }}
            />
            <Input
              aria-label={t('setup.tripStart')}
              type="datetime-local"
              value={tripStartAt}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTripStartAt(event.currentTarget.value)}
            />
            <Input
              aria-label={t('setup.tripEnd')}
              type="datetime-local"
              min={tripStartAt || undefined}
              value={tripEndAt}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTripEndAt(event.currentTarget.value)}
            />
            {tripStartAt && tripEndAt && tripEndAt < tripStartAt ? (
              <p style={{ color: designTokens.colors.error, fontSize: 12, margin: 0 }}>{t('setup.endAfterStart')}</p>
            ) : null}
            <Button type="button" disabled={!tripName.trim() || !tripStartAt || !tripEndAt || tripEndAt < tripStartAt || isSaving} onClick={() => void createTrip()}>
              {isSaving ? t('setup.creating') : t('setup.create')}
            </Button>
            {error ? <p style={{ color: designTokens.colors.error, fontSize: 12, margin: 0 }}>{error}</p> : null}
          </div>
        ) : null}

        {content.nextLabel ? (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Button type="button" variant="primary" onClick={handleNext}>
              {content.nextLabel}
            </Button>
          </div>
        ) : null}

        {guide.step === 'place' ? (
          <p style={{ color: designTokens.colors.outline, fontSize: 11, margin: '10px 0 0' }}>
            {t('setup.waiting')}
          </p>
        ) : null}
      </aside>
    </>
  );
}

// ─── Route target for /projects/new/setup and /projects/:slug/setup ──────────

export function SetupLauncher({ projectSlug = '' }: { projectSlug?: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    startSetupGuide(projectSlug);
    navigate(projectSlug ? `/projects/${projectSlug}` : '/projects', { replace: true });
  }, [navigate, projectSlug]);
  return null;
}
